import {
  InfiniteData,
  queryOptions,
  useQuery,
  useQueryClient,
  DefaultError,
} from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';
import {
  AutoscalerRebalancingPlan,
  RebalancingPlansResponse,
  RebalancingPlanStatusEnum,
} from '@cast/types';

import { REBALANCING_IN_PROGRESS_STATES } from 'constants/cluster';
import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

import { getRebalancePlansQueryKey } from './useRebalancePlansQuery';

const REBALANCE_PLAN_QUERY_KEY = 'external-cluster-rebalance-plan';

const getRebalancePlanQueryKey = ({
  clusterId,
  rebalancePlanId,
  includeOperations,
  includeConfigurations,
  tourStepId,
}: {
  clusterId?: string;
  rebalancePlanId?: string;
  includeOperations?: boolean;
  includeConfigurations?: boolean;
  tourStepId?: string | number | null;
} = {}) =>
  [
    REBALANCE_PLAN_QUERY_KEY,
    clusterId,
    rebalancePlanId,
    includeOperations,
    includeConfigurations,
    tourStepId,
  ] as const;

type UseRebalancePlanQuery = {
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  rebalancePlan?: AutoscalerRebalancingPlan;
  error?: DefaultError;
  refetch: () => void;
};

type UseRebalancePlanQueryProps = {
  clusterId?: string;
  rebalancePlanId?: string;
  includeOperations?: boolean;
  includeConfigurations?: boolean;
  enabled?: boolean;
  tag?: string | number;
  meta?: Record<string, unknown>;
  refetchInterval?: number | false;
};

const getRebalancePlanQueryOptions = ({
  clusterId,
  rebalancePlanId,
  includeOperations = false,
  includeConfigurations = false,
  enabled = true,
  tag,
  meta,
  refetchInterval: refetchIntervalOverride,
  queryClient,
  tourStepId,
}: UseRebalancePlanQueryProps & {
  queryClient: ReturnType<typeof useQueryClient>;
  tourStepId?: string | number | null;
}) =>
  queryOptions({
    queryKey: getRebalancePlanQueryKey({
      clusterId,
      rebalancePlanId,
      includeOperations,
      includeConfigurations,
      tourStepId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getRebalancingPlan(
        {
          clusterId: clusterId!,
          rebalancingPlanId: rebalancePlanId!,
          includeOperations,
          includeConfigurations,
        },
        { middlewareArgs: { requestMeta: { tag } } }
      );

      const plansQueryKey = getRebalancePlansQueryKey({
        clusterId,
        includeConfigurations,
      });
      const queryData: InfiniteData<RebalancingPlansResponse> | undefined =
        queryClient.getQueryData(plansQueryKey);

      if (queryData && includeConfigurations) {
        const newPages = queryData.pages.map((page) => {
          const index = page.items?.findIndex(
            (plan) => plan.rebalancingPlanId === rebalancePlanId
          );

          if (data && index !== undefined && index >= 0 && page.items) {
            page.items[index] = data;
          }

          return page;
        });

        queryClient.setQueryData(plansQueryKey, {
          pages: newPages,
          pageParams: queryData.pageParams,
        });
      }

      return data || null;
    },
    enabled: enabled && !!clusterId && !!rebalancePlanId,
    refetchInterval: ({ queryKey }) => {
      if (refetchIntervalOverride) {
        return refetchIntervalOverride;
      }

      const data = queryClient.getQueryData<any>(queryKey);
      if (
        data &&
        ![
          RebalancingPlanStatusEnum.generating,
          ...REBALANCING_IN_PROGRESS_STATES,
        ].includes(data.status)
      ) {
        return false;
      }
      return tourStepId ? 1000 : 5000;
    },
    meta,
  });

export const useRebalancePlanQuery = ({
  clusterId,
  rebalancePlanId,
  includeOperations = false,
  includeConfigurations = false,
  enabled = true,
  tag,
  meta,
  refetchInterval,
}: UseRebalancePlanQueryProps): UseRebalancePlanQuery => {
  const queryClient = useQueryClient();
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const {
    isLoading,
    data: rebalancePlan,
    error,
    refetch,
    isSuccess,
    isError,
  } = useQuery(
    getRebalancePlanQueryOptions({
      clusterId,
      rebalancePlanId,
      includeOperations,
      includeConfigurations,
      enabled,
      tag,
      meta,
      refetchInterval,
      queryClient,
      tourStepId,
    })
  );

  return {
    isError,
    isSuccess,
    isLoading,
    rebalancePlan,
    error: error || undefined,
    refetch,
  };
};
